import React, { useCallback, useEffect, useMemo, useState } from 'react'
import DataGridV2 from '../../../components/DataGridV2'
import { apps, LOCAL_STORAGE_GRID_KEYS } from '../../../_constants';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { alpha, IconButton, ListItemIcon, Menu, MenuItem, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useDispatch, useSelector } from 'react-redux';
import { formatDate, getLocalStorageItem, isCompleteAssignment, isReadOnly, setLocalStorageItem, useCanAssignmentIsReadOnly, useCanLockedInspection, useInternetStatusDetector } from '../../../_helpers';
import { INITIAL_ROW_SELECTION_MODEL } from '../../../components/DataGridV2/constants';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';
import { Add, Cancel, CheckCircle, Delete, MoreVert, Visibility } from '@mui/icons-material';
import {  getRepairRequestLocations } from '../../../apis/surveyingApis';
import { surveyActions } from '../../../_actions';
import RepairRequestInfoModal from './RepairRequestInfoModal';
import RepairRequestCustomRow from './RepairRequestCustomRow';
import { getIdsForGridSelectedRows } from '../../../_helpers/getIdsForGridSelectedRows';
import { getSurveyRepairRequestColumns } from './surveyRepairRequestUtils';

const SurveyRepairRequests = ({
    requests,
    showActionColumn,
    selectedInspection,
    inspection,
    isSecondSignature,
    setOfflineRepairRequestData,
    loading,
    setOpenRepairRequestMap,
    selectedAssignmentLockInfo,
    isAssignmentGrid = false
}) => {
    const theme = useTheme();
    const history = useHistory();
    const dispatch = useDispatch();
    const apiRef = useGridApiRef();
    const app = useSelector((state) => state.app);
    const { t, i18n } = useTranslation();
    const [readOnly, setReadOnly] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [canDeleteRequest, setCanDeleteRequest] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [infoType, setInfoType] = useState(null);
    const [isOpenInfoModal, setIsOpenInfoModal] = useState(false);
    const [locations, setLocations] = useState([]);
    const [selectedRequestId, setSelectedRequestId] = useState(null);
    const keyPrefix = app === apps.aftercare ? "AFTERCARE" : app === apps.constructionQuality ? "CONSTRUCTION_QUALITY" : "GENERAL";
    const [rowSelectionModel, setRowSelectionModel] = useState(INITIAL_ROW_SELECTION_MODEL);
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const open = Boolean(anchorEl);
    const canLockedInspection = useCanLockedInspection();
    const isReadOnlyAssignment = useCanAssignmentIsReadOnly();
    const classes = useStyles({ readOnly });
    const [paginationModel, setPaginationModel] = useState(() => getLocalStorageItem(
        LOCAL_STORAGE_GRID_KEYS.SURVEY_REPAIR_REQUESTS_PAGINATION[keyPrefix], undefined
    ))
    const { deleteRRLoading, selectedAssignment, wbProjectInfo } = useSelector((state) => state.surveying)
    const { selected } = useSelector((state) => state.buildings);
    const isOnline = useInternetStatusDetector();
     
    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const locationRes = await getRepairRequestLocations();
                const locations = locationRes.data.map(x => ({
                    label: x.name,
                    value: x.name
                }));
                setLocations(locations);
            } catch (e) {
                console.error("Error fetching locations:", e);
            } 
        };

        fetchLocations();
    }, []);

    useEffect(() => {
        if (selectedRequestId && !deleteRRLoading) {
            setSelectedRequestId(null);
        }
    }, [selectedRequestId, deleteRRLoading]);

    useEffect(() => {
        if (selectedInspection) {
            setReadOnly(
                !selectedInspection.hasRights ||
                (isSecondSignature
                    ? selectedInspection.secondSignatureDate
                    : isReadOnly(selectedInspection.status)) ||
                canLockedInspection
            );
        }
    }, [selectedInspection && selectedInspection.status]);



    const requestIds = useMemo(() => {
        return getIdsForGridSelectedRows(rowSelectionModel, requests)
    }, [rowSelectionModel, requests]);

  
    useEffect(() => {
        if (requestIds.length > 0 && selectedInspection) {
            const canDelete = requestIds.every((requestId) => {
                const request = requests.find((p) => p.requestId === requestId);
                if (!request) return false;

                const isOfflineRequest = (!isOnline && request.sync === 0) || isOnline;

                return (
                    isOfflineRequest &&
                    !request.completed &&
                    (!request.resolvers ||
                        !request.resolvers.some((r) => r.workOrderNumber))
                );
            });

            setCanDeleteRequest(
                canDelete &&
                !canLockedInspection &&
                (!selectedInspection.lockId ||
                    (selectedInspection.lockId &&
                        requestIds.every((requestId) => {
                            const request = requests.find((p) => p.requestId === requestId);
                            return request?.sync === 0;
                        })))
            );
        } else {
            setCanDeleteRequest(false);
        }
    }, [requestIds, requests, selectedInspection, canLockedInspection, isOnline]);


    const handleOpenMenu = (event, request) => {
        setAnchorEl(event.currentTarget);
        setSelectedRequest(request);
    };

    const handleCloseMenu = () => {
        setSelectedRequest(null);
        setAnchorEl(null);
    };

    const actionColumns = showActionColumn
        ? [
            {
                download: true,
                print: true,
                field: "action",
                headerName: t("general.action"),
                flex: 1,
                align: "start",
                minWidth: 100,
                renderCell: (params) => {
                    const request = params.row;
                    if (!request) return "";
                    return (
                        request &&
                        (!request.completed && !readOnly ? (
                            <IconButton
                                disabled={
                                    selectedInspection &&
                                    !selectedInspection.isSecondSignatureInitiated
                                }
                                aria-label="more"
                                aria-controls="long-menu"
                                aria-haspopup="true"
                                className={classes.moreButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenMenu(e, request);
                                }}
                                size="large"
                            >
                                <MoreVert />
                            </IconButton>
                        ) : (
                            <CheckCircle className={classes.colorSuccess} />
                        ))
                    );
                },
            },
        ]
        : [];

    const statusOptions = [
        ...new Set(requests.map((item) => item.status))
    ];

    const columns = getSurveyRepairRequestColumns({ t, keyPrefix, i18n, apiRef, actionColumns, inspection, locations, statusOptions })

    const handleRowClick = (selectedRow) => {
        if (isAssignmentGrid) {
            if (
                selectedAssignment &&
                selectedRow &&
                selectedRow.sync === 0 &&
                !isCompleteAssignment(selectedAssignment.internalStatus)
            ) {
                setOpenRepairRequestMap(true);
                setOfflineRepairRequestData(selectedRow);
            } else if (app === 3 && window.navigator.onLine && selectedRow.sync !== 0) {
                history.push(
                    `/werk/${selected?.projectNo}/kwaliteitsborging/opname/melding/${selectedRow.requestId}`
                );
            }
        }
        else {
            if (
                selectedRow &&
                selectedRow.sync === 0 &&
                !isReadOnly(selectedInspection.status)
            ) {
                setOpenRepairRequestMap(selectedRow);
            } else if (isOnline && selectedRow.sync !== 0) {
                switch (app) {
                    case apps.constructionQuality: {
                        history.push(
                            `/werk/${selectedInspection?.projectNo}/kwaliteitsborging/opname/melding/${selectedRow.requestId}`
                        );
                        break;
                    }
                    case apps.aftercare: {
                        history.push(`/nazorg/opname/melding/${selectedRow.requestId}`);
                        break;
                    }
                    default: {
                        break;
                    }
                }
            }
        }
    };

    const handleDeleteRR = () => {
        const requestsData = requests.filter((r) => requestIds.includes(r.requestId));
        if (requestsData.length > 0) {
            dispatch(surveyActions.deleteRepairRequestAction(requestsData, true));
        }
        setRowSelectionModel(INITIAL_ROW_SELECTION_MODEL);
    };

    const infoModalTogglerHandler = (type) => {
        !isOpenInfoModal && setAnchorEl(null);
        setInfoType(isOpenInfoModal ? null : type);
        setIsOpenInfoModal((p) => !p);
    };

    const getToolbarProps = useMemo(() => {
        const hasSelectedRows = requestIds.length > 0
        const toolbarStartActions = []
        if (isAssignmentGrid) {
            const canShowSurveyCreate =
                (selectedAssignmentLockInfo || (!selectedAssignmentLockInfo && isOnline)) &&
                selectedAssignment &&
                selectedAssignment.repairRequestRight &&
                wbProjectInfo &&
                !wbProjectInfo.archivedAt;
            if (canShowSurveyCreate) {
                toolbarStartActions.push({
                    title: t("survey.create.label"),
                    icon: Add,
                    onClick: () => setOpenRepairRequestMap(true),
                    disabled: isReadOnlyAssignment
                });
            }
        } else {
            if (!readOnly && !isSecondSignature) {
                toolbarStartActions.push({
                    title: t("general.add"),
                    icon: Add,
                    disabled: readOnly || canLockedInspection,
                    onClick: () => setOpenRepairRequestMap(),
                });
            }
        }

        const taskMenuListItems = (hasSelectedRows && !isAssignmentGrid)
            ? [
                {
                    title: t("general.delete"),
                    icon: (props) => <Delete color="error" {...props} />,
                    onClick: () => handleDeleteRR(),
                    disabled: !canDeleteRequest,
                },
            ]
            : [];


        return {
            toolbarStartActions,
            taskMenuListItems,
        };
    }, [rowSelectionModel, t, canDeleteRequest, selectedAssignmentLockInfo, isOnline, selectedAssignment, wbProjectInfo, isReadOnlyAssignment, setOpenRepairRequestMap, readOnly, isSecondSignature, canLockedInspection, isAssignmentGrid]);

    const getSurveyRepairRequestsForPrintAndDownload = ({ isAllSelected = true, idsToExport = [] }) => {
        const userSelectedRows = isAllSelected ? requests : idsToExport.map(id => requests?.find(item => item?.id === id));
        const results = userSelectedRows?.map(item => {
            const result = {};
            Object.keys(item).forEach(key => {
                let value = item[key];
                const isDateField = columns.some(x => x.field === key && x.type === 'date');
                if (isDateField) {
                    value = item[key] ? formatDate(new Date(item[key])) : "";
                }
                result[key] = value;
            });

            return result;
        });
        return results;
    };
    const onPaginationModelChange = useCallback((newPaginationModel) => {
        setPaginationModel(newPaginationModel)
        setLocalStorageItem(
            LOCAL_STORAGE_GRID_KEYS.SURVEY_REPAIR_REQUESTS_PAGINATION[keyPrefix],
            newPaginationModel
        );
    }, [keyPrefix])

    const handleCheckboxChange = useCallback((itemId) => {
        setRowSelectionModel((prevCheckedItems) => {
            const prevIds = Array.from(prevCheckedItems?.ids);
            let newIds = [];
            if (prevIds.includes(itemId)) {
                newIds = prevIds.filter((id) => id !== itemId);
            } else {
                newIds = [...prevIds, itemId];
            }
            return { ...prevCheckedItems, ids: new Set(newIds) };
        });
    }, []);

    return (
        <>
            <DataGridV2
                MobileCustomRowComponent={(props) => {
                    return <RepairRequestCustomRow
                        selectedCheckBoxes={Array.from(rowSelectionModel?.ids)}
                        handleCheckboxChange={handleCheckboxChange}
                        onRowClick={handleRowClick}
                        item={props?.row}
                        key={props.rowId}
                        canShowCheckBox={
                            isAssignmentGrid || (!isSecondSignature && !readOnly)
                        }
                    />
                }}
                localSearchIdentifier={LOCAL_STORAGE_GRID_KEYS.SURVEY_REPAIR_REQUESTS_SEARCH_TEXT[keyPrefix]}
                localColumnFilterIdentifier={LOCAL_STORAGE_GRID_KEYS.SURVEY_REPAIR_REQUESTS_COLUMN_FILTER[keyPrefix]}
                localColumnSortIdentifier={LOCAL_STORAGE_GRID_KEYS.SURVEY_REPAIR_REQUESTS_COLUMN_SORT[keyPrefix]}
                localColumnOrderIdentifier={LOCAL_STORAGE_GRID_KEYS.SURVEY_REPAIR_REQUESTS_COLUMN_ORDER[keyPrefix]}
                localColumnWidthIdentifier={LOCAL_STORAGE_GRID_KEYS.SURVEY_REPAIR_REQUESTS_COLUMN_WIDTH[keyPrefix]}
                localColumnVisibilityIdentifier={LOCAL_STORAGE_GRID_KEYS.SURVEY_REPAIR_REQUESTS_COLUMN_VISIBILITY[keyPrefix]}
                localDataGridViewIdentifier={LOCAL_STORAGE_GRID_KEYS.SURVEY_REPAIR_REQUESTS_DATAGRID_VIEW[keyPrefix]}
                apiRef={apiRef}
                checkboxSelection={isAssignmentGrid || (!isSecondSignature && !readOnly)}
                rows={requests}
                columns={columns}
                loading={loading}
                pagination
                filterMode="client"
                customRowHeight={isMobile ? 80 : 62}
                {...(!!paginationModel && { paginationModel })}
                onPaginationModelChange={onPaginationModelChange}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={(newRowSelectionModel) => {
                    setRowSelectionModel(newRowSelectionModel);
                }}
                toolbarProps={{
                    ...getToolbarProps,
                    selectedRows: rowSelectionModel,
                    getData: getSurveyRepairRequestsForPrintAndDownload,
                    title: t('general.repairRequests'),

                }}
                onRowClick={(params) => {
                    const requestId = params.row?.requestId;
                    const selectedRow = requests?.find((x) => x.requestId === requestId);
                    if (selectedRow) {
                        handleRowClick(selectedRow);
                    }
                }}

            />

            {isOpenInfoModal && (
                <RepairRequestInfoModal
                    selectedRequest={selectedRequest}
                    handleClose={infoModalTogglerHandler}
                    open={isOpenInfoModal}
                    infoType={infoType}
                />
            )}
            {selectedRequest && (
                <Menu
                    id="long-menu"
                    anchorEl={anchorEl}
                    keepMounted
                    open={open}
                    onClose={handleCloseMenu}
                >
                    <MenuItem
                        onClick={() =>
                            infoModalTogglerHandler(
                                selectedRequest.isRework ? "rework" : "reject"
                            )
                        }
                    >
                        <ListItemIcon style={{ minWidth: "40px" }}>
                            {!selectedRequest.isRework ? (
                                <Cancel color="error" size="small" />
                            ) : (
                                <Visibility color="primary" size="small" />
                            )}
                        </ListItemIcon>
                        <Typography>
                            {t(
                                selectedRequest.isRework
                                    ? t("repairRequest.viewRepairRequestRework")
                                    : t("general.button.reject")
                            )}
                        </Typography>
                    </MenuItem>
                    <MenuItem onClick={() => infoModalTogglerHandler("approve")}>
                        <ListItemIcon style={{ minWidth: "40px" }}>
                            <CheckCircle size="small" className={classes.colorSuccess} />
                        </ListItemIcon>
                        <Typography>{t("general.approve")}</Typography>
                    </MenuItem>
                </Menu>
            )}
        </>
    )
}

export default SurveyRepairRequests
const useStyles = makeStyles((theme) => ({
    "@global": {
        ".MuiButtonBase-root:focus": {
            outline: 0,
        },
    },
    customCheckbox: {
        [theme.breakpoints.down("md")]: {
            "& .MuiSvgIcon-root": {
                height: 14,
            },
        },
    },
    drawerHeader: {
        display: "flex",
        padding: theme.spacing(1),
        justifyContent: "space-between",
        alignItems: "center",
        paddingBottom: 7,
    },
    mainContainer: {
        height: "100%",
        padding: 0,
        backgroundColor: 'white'
    },
    surveyMomentCardTitle: {
        [theme.breakpoints.only("xs")]: {
            fontSize: 14,
        },
        [theme.breakpoints.only("sm")]: {
            fontSize: 16,
        },
        fontSize: 12,
        fontWeight: "bold",
        color: theme.palette.common.black,
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
    },
    drawerIcon: {
        fill: theme.palette.grey[300],
        cursor: "pointer",
    },
    stickyHeader: {
        position: "sticky",
        top: 0,
        background: theme.palette.common.white,
        zIndex: 101,
    },
    meldingenTableButton: {
        color: theme.palette.grey[700],
        "& svg": {
            [theme.breakpoints.down("sm")]: {
                fontSize: "16px !important",
            },
        },
        "& .MuiButton-label": {
            fontSize: 16,
            [theme.breakpoints.down("sm")]: {
                fontSize: "14px !important",
                lineHeight: "0px !important",
            },
        },
        "& .MuiButton-label > .MuiButton-startIcon": {
            [theme.breakpoints.down("sm")]: {
                marginRight: "4px !important",
            },
        },
    },
    meldingenTableDeleteButton: {
        marginRight: 10,
    },
    customMainHeader: {
        fontSize: 14,
        [theme.breakpoints.only("xs")]: {
            fontSize: 14,
        },
        [theme.breakpoints.only("sm")]: {
            fontSize: 16,
        },
        "& .MuiButtonBase-root": {
            [theme.breakpoints.only("xs")]: {
                fontSize: 14,
            },
            [theme.breakpoints.only("sm")]: {
                fontSize: 16,
            },
            fontSize: 14,
            padding: 0,
            margin: 0,
            whiteSpace: "nowrap ",
        },
    },
    papers: {
        minWidth: 100,
        flexGrow: 1,
        padding: theme.spacing(1),
        backgroundColor: theme.palette.common.white,
        borderRadius: 0,
        boxShadow: "none",
        [theme.breakpoints.down("md")]: {
            padding: theme.spacing(0, 1),
        },
        [theme.breakpoints.up("sm")]: {
            borderRight: `1px solid ${theme.palette.grey[300]}`,
            "&:nth-last-child(1)": {
                borderRight: `none`,
            },
        },
    },
    paperSubContainer: {
        display: "flex",
        justifyContent: "space-between",
        minHeight: 22,
    },
    cardTitle: {
        fontSize: 12,
        [theme.breakpoints.only("xs")]: {
            fontSize: 14,
        },
        [theme.breakpoints.only("sm")]: {
            fontSize: 16,
        },
        color: theme.palette.grey[500],
        whiteSpace: "wrap",
    },
    removePadding: {
        padding: `${theme.spacing(0)} !important`,
    },
    expandableRow: {
        background: theme.palette.common.white,
        boxShadow: "0.6px 1px 3px #ccc",
        display: "table-row",
        position: "relative",
        top: 0,
        borderRadius: 4,
        "& .MuiTableCell-root": {
            "&:first-child": {
                borderBottomLeftRadius: 4,
            },
            "&:last-child": {
                borderBottomRightRadius: 4,
            },
        },
    },
    wrapMomentTitle: {
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        lineClamp: 1,
        wordBreak: "break-all" /* number of lines to show */,
        display: "-webkit-box",
        boxOrient: "vertical",
    },
    wrapMomentContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    minCellWidth: {
        minWidth: 100,
    },
    customCell: {
        position: "relative",
    },
    dataTable: {
        paddingBottom: theme.spacing(1),
    },
    mapIcon: {
        fill: theme.palette.primary.main,
        fontSize: 18,
    },

    headerIconContainer: {
        display: "flex",
        alignItems: "center",
    },
    tooltipImageContainer: {
        padding: theme.spacing(1.25),
        "& > img": {
            maxWidth: "200px",
            maxHeight: "200px",
        },
    },
    colorSuccess: {
        padding: theme.spacing(0.2),
        color: theme.palette.success.main,
    },
    success: {
        backgroundColor: theme.palette.success.main,
        color: theme.palette.common.white,
        "&:hover": {
            backgroundColor: `${alpha(theme.palette.success.main, 0.7)} !important`,
        },
    },
    moreButton: {
        padding: theme.spacing(0.2),
    },
}));